import { useCallback, useEffect, useRef, useState } from 'react';

const DRAFT_KEY = 'fw:data-entry-draft:v1';
const AUTOSAVE_DELAY_MS = 800;

export interface DraftPayload {
  savedAt: number;
  [key: string]: any;
}

/** Reads a previously stored draft. Returns null when there isn't a usable one. */
export const readDraft = <T extends object>(): (T & DraftPayload) | null => {
  try {
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (error) {
    // Private browsing, cleared storage or a corrupt value — start fresh.
    console.warn('Could not read the saved draft:', error);
    return null;
  }
};

export const clearDraft = () => {
  try {
    window.localStorage.removeItem(DRAFT_KEY);
  } catch (error) {
    console.warn('Could not clear the saved draft:', error);
  }
};

interface UseEntryDraftOptions<T extends object> {
  /** Everything worth restoring: the in-progress form plus the pending entries */
  data: T;
  /** Autosave runs only while this is true */
  enabled: boolean;
  /** Skip autosaving an untouched form */
  shouldSave: boolean;
}

interface UseEntryDraftResult {
  savedAt: number | null;
  /** Writes immediately, outside the debounce (the Save Draft button) */
  saveNow: () => void;
  discard: () => void;
}

/**
 * Keeps the in-progress entry in this browser so a refresh or a stray navigation
 * doesn't lose a half-typed invoice. Debounced so typing doesn't thrash storage.
 */
export const useEntryDraft = <T extends object>({
  data,
  enabled,
  shouldSave
}: UseEntryDraftOptions<T>): UseEntryDraftResult => {
  const [savedAt, setSavedAt] = useState<number | null>(null);
  // Held in a ref so the debounce always writes the newest data without
  // restarting the timer on every keystroke's identity change.
  const dataRef = useRef(data);
  dataRef.current = data;

  const write = useCallback(() => {
    try {
      const savedAtMs = Date.now();
      window.localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ ...dataRef.current, savedAt: savedAtMs })
      );
      setSavedAt(savedAtMs);
    } catch (error) {
      console.warn('Could not save the draft:', error);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !shouldSave) return undefined;
    const timer = window.setTimeout(write, AUTOSAVE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [data, enabled, shouldSave, write]);

  const discard = useCallback(() => {
    clearDraft();
    setSavedAt(null);
  }, []);

  return { savedAt, saveNow: write, discard };
};
